import React, { useCallback, useMemo } from 'react';
import type { ISelectBoxOptions } from 'devextreme-react/select-box';
import { DataStoreOptions } from '#/common/hooks/useDataStore';
import type { EditorProps } from './EditorProps';
import RelatedItems, { RelatedItemsOptions } from './RelatedItems';
import SelectCard from '#/common/components/SelectCard';
import dotWalk from '#/common/utils/dotWalk';

interface RelatedItemsCardsOptions extends ISelectBoxOptions {
  valueExpr?: string;
  displayExpr?: string;
  placeholder?: string;
  readOnly?: boolean;
  validationRules?: any[];
  label?: string;
  dropdownColumns?: 1 | 2;
  button?: {
    hide?: boolean;
    tooltip?: string;
    url?: string;
    icon?: string;
    desktopOnly?: boolean;
    readOnly?: boolean;
  };
  dataStoreOptions?: Pick<DataStoreOptions<unknown>, 'getItemList' | 'getItem' | 'filter'>;
  cardConfig?: {
    iconName?: string;
    iconColor?: string;
    iconHolderOpacity?: number;
    subtitleExpr?: string;
  };
  dynamic?: {
    watch: string[];
    build: (ctx: { record: Record<string, any>; value: any }) => any | null;
    onWatchChange?: (args: {
      changed: { field: string; value: any; prev?: any }[];
      record: Record<string, any>;
      value: any;
      form: any;
    }) => void;
  };
}

export interface RelatedItemsCardsProps {
  editorType: 'RelatedItemsCards';
  editorOptions: RelatedItemsCardsOptions;
}

const RelatedItemsCards: React.FC<EditorProps<RelatedItemsCardsOptions>> = (props) => {
  const { editorOptions } = props;
  const { cardConfig, dropdownColumns = 2, ...restOptions } = editorOptions;

  // Custom item render using SelectCard with icon support
  const itemRender = useCallback(
    (
      data: Record<string, unknown>,
      valueExpr: string,
      displayExpr?: string | ((item: any) => string),
    ) => {
      if (!data) return null;

      const id = String(data[valueExpr] || '');
      const title = displayExpr
        ? typeof displayExpr === 'string'
          ? String(dotWalk(data, displayExpr) || 'Unnamed')
          : String(displayExpr(data) || 'Unnamed')
        : 'Unnamed';
      const subtitle = cardConfig?.subtitleExpr
        ? String(dotWalk(data, cardConfig.subtitleExpr) || '')
        : undefined;

      return (
        <SelectCard
          id={id}
          iconName={cardConfig?.iconName}
          title={title}
          subtitle={subtitle}
          iconColor={cardConfig?.iconColor}
          iconHolderOpacity={cardConfig?.iconHolderOpacity}
        />
      );
    },
    [cardConfig],
  );

  // Determine column class based on dropdownColumns
  const dropdownClassName = useMemo(() => {
    return dropdownColumns === 1 ? 'cards-single-column' : 'cards-two-columns';
  }, [dropdownColumns]);

  const modifiedOptions: RelatedItemsOptions = {
    ...restOptions,
    customItemRender: itemRender,
    dropdownClassName,
  };

  return <RelatedItems {...props} editorOptions={modifiedOptions} />;
};

export default React.memo(RelatedItemsCards);
